import React from "react";
import { isNil, size } from "lodash";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AiccFeedbackDailyPoint } from "types/model/aiccCustomer";
import ChartCard from "./ChartCard";
import TooltipBox from "./TooltipBox";
import { formatAxisDate, formatTooltipDate } from "./formatChartDate";
import {
  AXIS_STROKE,
  AXIS_TICK,
  CHART_COLORS,
  CHART_HEIGHT,
  CHART_MARGIN,
  CHART_SURFACE,
  GRID_STROKE,
  MAX_DOTTED_POINTS,
} from "./chartTheme";

interface Props {
  data: AiccFeedbackDailyPoint[];
  isLoading?: boolean;
}

function SatisfactionRateChart({ data, isLoading }: Props) {
  const showDots = size(data) <= MAX_DOTTED_POINTS;

  return (
    <ChartCard
      title="Response Satisfaction Rate"
      description="Thumbs Up / (Thumbs Up + Thumbs Down) - daily"
      isLoading={isLoading}
      hasData={size(data) > 0}
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <LineChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid vertical={false} stroke={GRID_STROKE} />
          <XAxis
            dataKey="date"
            tickFormatter={formatAxisDate}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: AXIS_STROKE }}
            minTickGap={24}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(value: number) => `${value}%`}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          <Tooltip
            content={({ active, payload }) => {
              const point = payload?.[0]?.payload as AiccFeedbackDailyPoint | undefined;
              if (!active || !point) {
                return null;
              }

              return (
                <TooltipBox
                  title={formatTooltipDate(point.date)}
                  rows={[
                    {
                      label: "Satisfaction rate",
                      value: isNil(point.satisfactionRate) ? "No feedback" : `${point.satisfactionRate}%`,
                      color: CHART_COLORS.neutral,
                    },
                    { label: "👍 Thumbs Up", value: String(point.thumbsUp) },
                    { label: "👎 Thumbs Down", value: String(point.thumbsDown) },
                  ]}
                />
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="satisfactionRate"
            stroke={CHART_COLORS.neutral}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            connectNulls={false}
            dot={showDots ? { r: 4, fill: CHART_COLORS.neutral, stroke: CHART_SURFACE, strokeWidth: 2 } : false}
            activeDot={{ r: 5, fill: CHART_COLORS.neutral, stroke: CHART_SURFACE, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export default SatisfactionRateChart;
